import { useMemo } from "react";
import { useGetGamificationsQuery } from "../../../../../services/gamification";
import {
  CampaignType,
  GamificationSortBy,
  type IGamification,
  type IGamificationCampaignDto,
  type IProbability,
} from "../../../../../@types/gamification";
import { Meta } from "../../../../../@types/paginated";
import { mapStatusToFrontend } from "../../utils/status-mapper";

interface UseCampaignsDataParams {
  search?: string;
  status?: string;
  orderBy?: string;
  sortBy?: GamificationSortBy;
  sortOrder?: "ASC" | "DESC";
  startDate?: string;
  endDate?: string;
  campaignType?: CampaignType;
  includeDeleted?: boolean;
  meta: { page: number; perPage?: number };
}

function probabilitiesOf(item: IGamificationCampaignDto): IProbability[] {
  return Array.isArray(item.discountProbabilities)
    ? item.discountProbabilities
    : item.discountProbabilities.intervals;
}

function mapCampaign(item: IGamificationCampaignDto): IGamification {
  return {
    id: item.id,
    idCapa: item.idCapa,
    nickname: item.campaignName,
    slug: item.slug ?? undefined,
    startDate: item.startDate,
    endDate: item.endDate,
    status: mapStatusToFrontend(item.status, item.deletedAt ?? null),
    campaignType: item.mechanic,
    mechanic: item.mechanic,
    mechanicConfig: item.mechanicConfig,
    stores: item.stores.map((store) => ({
      storeCode: String(store.code),
      storeName: store.name,
      nickname: store.nickname,
    })),
    allStoresSelected: item.allStoresSelected,
    hasRegulationFile: item.hasRegulationFile,
    hasBanner3x4: item.hasBanner3x4,
    hasBanner16x9: item.hasBanner16x9,
    banner3x4Url: item.banner3x4Url,
    banner16x9Url: item.banner16x9Url,
    probabilities: probabilitiesOf(item),
    regulations: item.regulations,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    deletedAt: item.deletedAt ?? undefined,
  };
}

export const useCampaignsData = (params: UseCampaignsDataParams) => {
  const { data: rawData, isLoading, isFetching, error, refetch } =
    useGetGamificationsQuery(params);

  const campaignsData = useMemo(() => {
    if (!rawData) return { campaigns: [], meta: {} as Meta };

    const meta: Meta = {
      currentPage: rawData.pagination.page,
      lastPage: rawData.pagination.totalPages,
      perPage: rawData.pagination.limit,
      total: rawData.pagination.total,
      prev: rawData.pagination.hasPrevious ? rawData.pagination.page - 1 : null,
      next: rawData.pagination.hasNext ? rawData.pagination.page + 1 : null,
    };

    return { campaigns: rawData.data.map(mapCampaign), meta };
  }, [rawData]);

  return { ...campaignsData, isLoading, isFetching, error, refetch };
};
